import * as React from 'react';
import { useState } from 'react';
import moment from 'moment';
import { connect } from 'react-redux';
import { Button, DatePicker, Form, Input, InputNumber, message, Modal, Select } from 'antd';
import { ExclamationCircleOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons';
import OWButton from '../ow_button/OWButton';
import OWUpload from '../OWUpload';
import { OWSubTabs } from '../ow_sub_tabs/OWSubTabs';
import { withUserType } from '../withUserType';
import DeliverableAssigneeSelect from '../deliverable_assignee_select/DeliverableAssigneeSelect';
import DeliverableWorkOrders from '../deliverable_work_orders/DeliverableWorkOrders';
import DeliverableTasks from '../deliverable_tasks/DeliverableTasks';
import { EmptyState } from '../empty_state/EmptyState';
import { DELIVERABLE_STATUSES } from '../../constants/DeliverableStatuses';
import { PROJECT_BUDGET_TYPES } from '../../constants/ProjectEntities';
import { getCascadeDueDatesValue } from '../project_cascade_due_dates_field/ProjectCascadeDueDatesField';
import { ROLE_TYPES } from '../../utils/DataConstants';
import {
	getCurrency,
	getEntityById,
	nullSafeGet,
	nullSafeGetOrElse,
} from '../../utils/DataAccessUtils';
import { normFile, mediaUploadValidation, isVideoMimeType } from '../../utils/ImageUtils';
import { getBackendUri } from '../../utils/EnvConfigUtils';
import { retrieveCachedUserDetails } from '../../thunks/session_thunks';
import {
	deliverablesRestCrudThunksForBuyer,
	deliverablesRestCrudThunksForSupplier,
	shiftDeliverableDueDate,
} from '../../thunks/deliverables_thunks';
import {
	DELIVERABLES_GRAPH_TC,
	getAllDependents,
	getDependencyOptions,
} from '../../utils/DeliverableUtils';
import { getRecordsForTargetCollection } from '../../reducers/standard_reducer_utils';

const { TextArea } = Input;

const CONTACTS_TC = 'DELIVERABLE_FORM_CONTACTS_AUTOCOMPLETE';

// Existing gallery -> antd Upload fileList (so edit shows current media).
// Preserve fileType so a re-save keeps videos tagged as videos.
const galleryToFileList = (gallery = []) =>
	gallery.map((item, index) => ({
		uid: `${nullSafeGetOrElse('fileId', item, index)}`,
		name: nullSafeGetOrElse('fileName', item, ''),
		status: 'done',
		fileId: nullSafeGetOrElse('fileId', item, ''),
		fileName: nullSafeGetOrElse('fileName', item, ''),
		fileType: nullSafeGetOrElse('fileType', item, 'image'),
	}));

// antd Upload fileList -> backend GalleryItem[]. New uploads carry a MIME type on
// `file.type`; existing items carry a saved `fileType` — use either to tag video vs image.
const fileListToGallery = (fileList = []) =>
	(fileList || [])
		.map((file) => {
			const fileId = nullSafeGetOrElse(
				'response.data.fileId',
				file,
				nullSafeGetOrElse('fileId', file, '')
			);
			const fileName = nullSafeGetOrElse(
				'response.data.fileName',
				file,
				nullSafeGetOrElse('fileName', file, nullSafeGetOrElse('name', file, ''))
			);
			const fileType =
				nullSafeGetOrElse('fileType', file, null) ||
				(isVideoMimeType(nullSafeGet('type', file)) ? 'video' : 'image');
			return { fileId, fileName, fileType };
		})
		.filter((item) => item.fileId);

// antd Upload fileList -> backend attachments [{ fileId, fileName }].
const fileListToAttachments = (fileList = []) =>
	(fileList || [])
		.map((file) => {
			const fileId = nullSafeGetOrElse(
				'response.data.fileId',
				file,
				nullSafeGetOrElse('fileId', file, '')
			);
			const fileName = nullSafeGetOrElse(
				'response.data.fileName',
				file,
				nullSafeGetOrElse('fileName', file, nullSafeGetOrElse('name', file, ''))
			);
			return { fileId, fileName };
		})
		.filter((item) => item.fileId);

// Build the form's initial values from the (live) deliverable record. The modal is
// mounted fresh each time it opens (parent renders it only while open), so these seed
// the fields once on mount — no visible-watching effect needed.
const buildInitialValues = (deliverable) => ({
	title: nullSafeGetOrElse('name', deliverable, ''),
	assignedTo: nullSafeGetOrElse('assignedTo', deliverable, []),
	dueDate: nullSafeGet('dueDate', deliverable) ? moment(nullSafeGet('dueDate', deliverable)) : null,
	dependsOnDeliverableId: nullSafeGet('dependsOnDeliverableId', deliverable),
	budget: nullSafeGet('budget', deliverable),
	description: nullSafeGetOrElse('description', deliverable, ''),
	images: galleryToFileList(nullSafeGetOrElse('gallery', deliverable, [])),
	attachments: galleryToFileList(nullSafeGetOrElse('attachments', deliverable, [])),
});

// The "New Deliverable" / "Edit Deliverable" modal (Figma 13207-68101).
const DeliverableFormModal = ({
	projectId,
	deliverable,
	onClose,
	onSaved,
	userType,
	currentUser,
	projectBudgetType,
	projectCascadesDueDates,
	projectDeliverables,
	createDeliverable,
	updateDeliverable,
	shiftDueDate,
}) => {
	const [form] = Form.useForm();
	const isEdit = !!nullSafeGet('id', deliverable);
	const deliverableId = nullSafeGet('id', deliverable);

	// Depending on one of your own dependents is the one way to make a cycle, and the
	// API rejects it with a 400 — so those are simply not offered.
	const dependencyOptions = getDependencyOptions(deliverableId, projectDeliverables);
	// Everything the backend will drag along when this deliverable's due date moves.
	const dependents = isEdit ? getAllDependents(deliverableId, projectDeliverables) : [];
	const savedDueDate = nullSafeGet('dueDate', deliverable);
	const dueDateValue = Form.useWatch('dueDate', form);
	// A move only cascades when there is a stored date to measure the delta from —
	// setting a first due date shifts nothing, which is what the API does too.
	const dueDateMoved =
		isEdit &&
		!!savedDueDate &&
		!!dueDateValue &&
		!moment(dueDateValue).isSame(moment(savedDueDate), 'day');
	// The known dependents drive the pre-save warning only. The cascade itself is
	// gated on the date having moved, never on this count: our copy of the graph can
	// be empty (still loading, request failed, project past the fetch's page size)
	// and skipping the PATCH would silently leave the whole chain behind.
	// The project can switch cascading off (cascadeDependentDueDates). Absent on the
	// record reads as on, which is how projects behaved before the setting existed.
	const cascades = dueDateMoved && projectCascadesDueDates !== false;
	const willCascade = cascades && dependents.length > 0;
	const cascadeDays = dueDateMoved
		? moment(dueDateValue).startOf('day').diff(moment(savedDueDate).startOf('day'), 'days')
		: 0;
	// Per-deliverable budget only applies when the project uses individual budgets.
	const showBudget = projectBudgetType === PROJECT_BUDGET_TYPES.individual;

	const [submitting, setSubmitting] = useState(false);
	const [activeTab, setActiveTab] = useState('overview');

	// Block submit while any gallery image or document is still uploading.
	const imagesValue = Form.useWatch('images', form);
	const attachmentsValue = Form.useWatch('attachments', form);
	const isUploading = [...(imagesValue || []), ...(attachmentsValue || [])].some(
		(file) => file && file.status === 'uploading'
	);

	const currency = getCurrency({ currentUser });
	const currencyId = currency ? currency.id : 'USD';
	const uploadHeaders = { 'X-Auth-Token': retrieveCachedUserDetails(['token']).token };
	const uploadUrl = `${getBackendUri()}/api/v1/${userType}/file/upload`;

	const handleSubmit = () => {
		form
			.validateFields()
			.then((values) => {
				setSubmitting(true);
				// Status is intentionally NOT sent — it is backend-calculated.
				// projectId / ids / budget must be numbers: the backend casts these
				// request-map values to numeric types and rejects strings
				// (e.g. reqValueLong("projectId") / optValueBigDecimal("budget")).
				const entity: any = {
					id: nullSafeGet('id', deliverable),
					projectId: Number(projectId),
					name: values.title,
					description: values.description,
					// Backend parses dueDate as a String (DateTime.parse) — send ISO, not epoch.
					dueDate: values.dueDate ? values.dueDate.startOf('day').toISOString() : null,
					budget: values.budget != null ? Number(values.budget) : undefined,
					assignedTo: values.assignedTo,
					workOrderIds: nullSafeGetOrElse('workOrderIds', deliverable, []).map((id) => Number(id)),
					gallery: fileListToGallery(values.images),
					attachments: fileListToAttachments(values.attachments),
					// null clears the dependency; the API reads absent the same way, but being
					// explicit keeps "I removed it" distinct from "I didn't touch it".
					dependsOnDeliverableId:
						values.dependsOnDeliverableId != null ? Number(values.dependsOnDeliverableId) : null,
				};
				// New deliverables start as Not Started (hardcoded from the FE); status
				// is otherwise backend-managed, so we don't send it on update.
				if (!isEdit) {
					entity.status = DELIVERABLE_STATUSES.notStarted;
				}
				const action = isEdit ? updateDeliverable : createDeliverable;
				const messageKey = 'deliverable-form-op';
				message.loading({
					content: isEdit ? 'Saving deliverable…' : 'Creating deliverable…',
					key: messageKey,
				});
				// A PUT sets this deliverable's date without touching its dependents, so when
				// the date moved, shift first: the API derives the delta from the STORED date,
				// which the PUT is about to overwrite. The PATCH answers with every deliverable
				// it touched, this one first — so the count we report is the server's, not our
				// guess from the local graph.
				const save = cascades
					? shiftDueDate(deliverableId, entity.dueDate).then((shifted: any) =>
							action(entity).then((saved) => ({
								saved,
								movedCount: Math.max(0, nullSafeGetOrElse('length', shifted, 1) - 1),
							}))
					  )
					: action(entity).then((saved) => ({ saved, movedCount: 0 }));
				save
					.then(({ saved, movedCount }) => {
						message.success({
							content: movedCount
								? `Deliverable updated — ${movedCount} dependent ${
										movedCount === 1 ? 'deliverable' : 'deliverables'
								  } moved`
								: isEdit
								? 'Deliverable updated'
								: 'Deliverable created',
							key: messageKey,
							duration: 1.5,
						});
						if (onSaved) {
							onSaved(saved);
						}
						onClose();
					})
					// Surface the API's own message when there is one — a rejected dependency
					// (cycle, cross-project) explains itself far better than a generic failure.
					.catch((error) =>
						message.error({
							content: typeof error === 'string' && error ? error : 'Failed to save deliverable.',
							key: messageKey,
						})
					)
					.finally(() => setSubmitting(false));
			})
			.catch(() => {
				// validation errors are surfaced inline by antd
			});
	};

	const linkedCount = nullSafeGetOrElse('workOrderIds', deliverable, []).length;

	return (
		<Modal
			visible
			width={1080}
			title={isEdit ? 'Edit Deliverable' : 'New Deliverable'}
			onCancel={onClose}
			footer={[
				<OWButton key="cancel" type="default" size="large" onClick={onClose}>
					Cancel
				</OWButton>,
				<OWButton
					key="submit"
					type="primary"
					size="large"
					loading={submitting || isUploading}
					onClick={handleSubmit}
				>
					{isEdit ? 'Save' : 'Create Deliverable'}
				</OWButton>,
			]}
		>
			<Form form={form} layout="vertical" initialValues={buildInitialValues(deliverable)}>
				<div className="flex gap-8">
					<div className="w-[320px] shrink-0">
						<Form.Item
							label="Name"
							name="title"
							rules={[{ required: true, message: 'Name is required' }]}
						>
							<Input placeholder="Deliverable name" />
						</Form.Item>
						<Form.Item
							label="Assigned to"
							name="assignedTo"
							rules={[{ required: true, message: 'Assign at least one person' }]}
						>
							<DeliverableAssigneeSelect targetCollectionName={CONTACTS_TC} />
						</Form.Item>
						<Form.Item
							label="Depends on (optional)"
							name="dependsOnDeliverableId"
							extra={
								dependents.length
									? `${dependents.length} ${
											dependents.length === 1 ? 'deliverable depends' : 'deliverables depend'
									  } on this one.`
									: null
							}
						>
							<Select
								allowClear
								showSearch
								optionFilterProp="children"
								placeholder="No dependency"
								disabled={dependencyOptions.length === 0}
								style={{ width: '100%' }}
							>
								{dependencyOptions.map((option) => (
									<Select.Option key={nullSafeGet('id', option)} value={nullSafeGet('id', option)}>
										{nullSafeGet('name', option)}
									</Select.Option>
								))}
							</Select>
						</Form.Item>
						<Form.Item
							label="Due Date (optional)"
							name="dueDate"
							// The consequence of this date belongs to the date field: `extra` puts it
							// directly under the picker, where a sibling block would sit a full
							// Form.Item margin away and read as unrelated.
							extra={
								willCascade ? (
									// footnoteregular (12/20) on the wrapper so the icon inherits the text's
									// size instead of .ant-form-item-extra's 14px, and leading-[20px] gives it
									// the same line box as the first line of text — no margin nudge needed.
									<span className="footnoteregular flex items-start gap-2">
										<ExclamationCircleOutlined className="text-[12px] !leading-[20px] text-sunsetOrange-8" />
										{/* Colour on the text nodes, not the wrapper: antd's .ant-form-item-extra
										    colour would otherwise win by source order. */}
										<span className="text-sunsetOrange-8">
											{`Saving moves ${dependents.length} dependent ${
												dependents.length === 1 ? 'deliverable' : 'deliverables'
											} by ${Math.abs(cascadeDays)} ${
												Math.abs(cascadeDays) === 1 ? 'day' : 'days'
											} ${cascadeDays > 0 ? 'later' : 'earlier'}.`}
										</span>
									</span>
								) : null
							}
						>
							<DatePicker format="MMM D, YYYY" style={{ width: '100%' }} />
						</Form.Item>
						{showBudget ? (
							<Form.Item
								label="Budget"
								name="budget"
								rules={[{ required: true, message: 'Budget is required' }]}
							>
								<InputNumber min={0} style={{ width: '100%' }} addonAfter={currencyId} />
							</Form.Item>
						) : null}
					</div>

					<div className="flex-1">
						<OWSubTabs
							tabs={[
								{ key: 'overview', label: 'Overview' },
								{ key: 'tasks', label: 'Tasks' },
								{ key: 'workOrders', label: `Work Orders (${linkedCount})` },
							]}
							activeKey={activeTab}
							onChange={setActiveTab}
							className="mb-4"
						/>
						{activeTab === 'overview' ? (
							<>
								<Form.Item label="Description" name="description">
									{/* TODO: swap for the shared rich-text editor used on WO/notes descriptions */}
									<TextArea rows={6} placeholder="Add a description" />
								</Form.Item>
								<Form.Item
									label="Gallery"
									name="images"
									valuePropName="fileList"
									getValueFromEvent={normFile}
								>
									<OWUpload
										accept="image/*,video/*"
										name="file"
										listType="picture-card"
										multiple
										headers={uploadHeaders}
										action={uploadUrl}
										beforeUpload={mediaUploadValidation}
									>
										<div style={{ display: 'inline' }}>
											<PlusOutlined />
											<div className="ant-upload-text">Upload</div>
										</div>
									</OWUpload>
								</Form.Item>
								<Form.Item
									label="Documents"
									name="attachments"
									valuePropName="fileList"
									getValueFromEvent={normFile}
								>
									<OWUpload name="file" multiple headers={uploadHeaders} action={uploadUrl}>
										<Button icon={<UploadOutlined />}>Upload documents</Button>
									</OWUpload>
								</Form.Item>
							</>
						) : null}
						{activeTab === 'tasks' ? (
							isEdit ? (
								<DeliverableTasks
									projectId={nullSafeGet('projectId', deliverable)}
									deliverableId={nullSafeGet('id', deliverable)}
									metrics={nullSafeGet('taskMetrics', deliverable)}
								/>
							) : (
								<EmptyState
									headline="Save first"
									body={<p>Create the deliverable, then add tasks to it.</p>}
								/>
							)
						) : null}
						{activeTab === 'workOrders' ? (
							isEdit ? (
								<DeliverableWorkOrders deliverable={deliverable} />
							) : (
								<EmptyState
									headline="Save first"
									body={<p>Create the deliverable, then link work orders to it.</p>}
								/>
							)
						) : null}
					</div>
				</div>
			</Form>
		</Modal>
	);
};

const mapStateToProps = (state, ownProps) => {
	const project = ownProps.projectId ? getEntityById(ownProps.projectId, state.projects) : null;
	return {
		currentUser: state.session.currentUser,
		// Read the live deliverable by id so link/unlink from inside the modal is
		// reflected on save (no frozen snapshot). Null when creating.
		deliverable: ownProps.deliverableId
			? getEntityById(ownProps.deliverableId, state.deliverables)
			: null,
		// Deliverables only carry their own budget when the project is on
		// "individual" budget; read it off the project keyed by id, not the shared detail slot.
		projectBudgetType: nullSafeGet('budgetType', project),
		// Whether a due-date move should take the dependent chain with it. Read through
		// the shared helper — a plain nullSafeGetOrElse would turn a stored `false` back
		// into `true` and re-show the cascade warning on a cascade-off project.
		projectCascadesDueDates: getCascadeDueDatesValue(project),
		// Every deliverable of the project, unfiltered — the dependency picker's options
		// and the dependents a due-date move will shift both come from it. Reading the
		// list's own collection instead would hide anything the user filtered out.
		projectDeliverables: getRecordsForTargetCollection(state.deliverables, DELIVERABLES_GRAPH_TC),
	};
};

const mapDispatchToProps = (dispatch, ownProps) => ({
	createDeliverable: (entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? deliverablesRestCrudThunksForSupplier.create(entity)
				: deliverablesRestCrudThunksForBuyer.create(entity)
		),
	updateDeliverable: (entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? deliverablesRestCrudThunksForSupplier.update(entity)
				: deliverablesRestCrudThunksForBuyer.update(entity)
		),
	shiftDueDate: (deliverableId, dueDate) =>
		dispatch(shiftDeliverableDueDate(ownProps.userType)(deliverableId, dueDate)),
});

export default withUserType(connect(mapStateToProps, mapDispatchToProps)(DeliverableFormModal));
